/**
 * Shared voucher settlement helpers (list view, detail view, reports).
 */

/** True when API attached a real Invoices document (not an empty placeholder). */
export function hasPersistedInvoice(voucher) {
  const inv = voucher?.invoice;
  if (!inv || typeof inv !== "object") return false;
  if (Object.keys(inv).length === 0) return false;
  return inv._id != null && inv.derived_from_voucher !== true;
}

/** Invoice row missing; API may still send derived_from_voucher payload or nothing. */
export function isInvoicePayloadMissing(voucher) {
  const inv = voucher?.invoice;
  if (!inv || typeof inv !== "object") return true;
  if (Object.keys(inv).length === 0) return true;
  if (hasPersistedInvoice(voucher)) return false;
  return !inv.derived_from_voucher;
}

export function isInvoiceSettled(inv) {
  if (!inv || typeof inv !== "object") return false;
  if (String(inv.payment_status || "").toLowerCase() === "cancelled") return false;
  const pending = Number(inv.pending_amount ?? NaN);
  if (Number.isFinite(pending) && pending <= 0) return true;
  if (inv.is_paid === true) return true;
  if (String(inv.payment_status || "").toLowerCase() === "paid") return true;
  return false;
}

export function isVoucherSettled(voucher) {
  if (!voucher) return false;
  if (voucher.voucher_type === "Receipt" || voucher.receipt) return true;

  if (hasPersistedInvoice(voucher) || voucher.invoice?.derived_from_voucher) {
    return isInvoiceSettled(voucher.invoice);
  }

  if (isInvoicePayloadMissing(voucher)) {
    const paid = Number(voucher.paid_amount) || 0;
    return paid > 0;
  }

  return isInvoiceSettled(voucher.invoice);
}

export function voucherPendingAmount(voucher) {
  if (!voucher) return 0;
  if (voucher.voucher_type === "Receipt" || voucher.receipt) return 0;
  if (isVoucherSettled(voucher)) return 0;

  if (hasPersistedInvoice(voucher) || voucher.invoice?.derived_from_voucher) {
    const pending = Number(voucher.invoice?.pending_amount);
    return Number.isFinite(pending) ? pending : 0;
  }

  return 0;
}

export function voucherTotalAmount(voucher) {
  if (!voucher) return 0;
  if (voucher.voucher_type === "Receipt") return Number(voucher.paid_amount) || 0;

  if (hasPersistedInvoice(voucher) || voucher.invoice?.derived_from_voucher) {
    const total = Number(voucher.invoice?.total_amount);
    if (Number.isFinite(total)) return total;
  }

  if (isInvoicePayloadMissing(voucher)) {
    const paid = Number(voucher.paid_amount) || 0;
    if (paid > 0) return paid;
  }

  const total = Number(voucher?.invoice?.total_amount);
  return Number.isFinite(total) ? total : 0;
}

export function voucherPaymentDateValue(voucher) {
  if (!voucher) return null;
  if (voucher.voucher_type === "Receipt") return voucher.voucher_date;

  if (voucher.invoice?.payment_date) return voucher.invoice.payment_date;

  if (isInvoicePayloadMissing(voucher) && Number(voucher.paid_amount) > 0) {
    return voucher.voucher_date;
  }

  if (voucher.invoice?.derived_from_voucher && voucher.invoice?.payment_date) {
    return voucher.invoice.payment_date;
  }

  return null;
}

export function voucherStatusMeta(voucher) {
  if (isVoucherSettled(voucher)) {
    return { label: "Paid", tone: "success", key: "paid" };
  }
  if (voucher?.is_active === false) {
    return { label: "Expired", tone: "danger", key: "overdue" };
  }
  const status = voucher?.invoice?.payment_status || "Pending";
  const map = {
    Paid: { label: "Paid", tone: "success", key: "paid" },
    Pending: { label: "Pending", tone: "warning", key: "pending" },
    Partial: { label: "Partial", tone: "navy", key: "pending" },
    Overdue: { label: "Overdue", tone: "danger", key: "overdue" },
    Cancelled: { label: "Cancelled", tone: "neutral", key: "overdue" },
  };
  return map[status] || { label: status, tone: "neutral", key: "pending" };
}
